import type { RuntimeWidgetConfig } from "../../core/config/public-config.types.ts";
import { getFeaturePanelDefinition } from "../features/feature-panel-registry.ts";

const schemaVersion = 1;
const storageNamespace = "migrosone-a11y:preferences:v1";

export type PreferenceValues = Record<string, unknown>;

export type WidgetPreferenceStorage = {
  key: string;
  read(): PreferenceValues;
  reset(): boolean;
  write(values: PreferenceValues): boolean;
};

export type CreatePreferenceStorageInput = {
  config: RuntimeWidgetConfig;
  storage?: Storage | null;
};

type PreferencePayload = {
  projectId: string;
  requestedHostname: string;
  schemaVersion: typeof schemaVersion;
  values: PreferenceValues;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getEnabledKnownFeatureKeys(config: RuntimeWidgetConfig) {
  return new Set(
    Object.entries(config.features)
      .filter(([, feature]) => feature.enabled === true)
      .map(([key]) => key)
      .filter((key) => getFeaturePanelDefinition(key)),
  );
}

function isSerializable(value: unknown) {
  try {
    JSON.stringify(value);
    return true;
  } catch {
    return false;
  }
}

function getDefaultStorage() {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export function buildPreferenceStorageKey(config: RuntimeWidgetConfig) {
  return `${storageNamespace}:${config.project.id}:${config.domain.requestedHostname}`;
}

export function sanitizePreferenceValues(
  values: PreferenceValues,
  config: RuntimeWidgetConfig,
): PreferenceValues {
  const allowedFeatureKeys = getEnabledKnownFeatureKeys(config);
  const sanitized: PreferenceValues = {};

  for (const [key, value] of Object.entries(values)) {
    if (!allowedFeatureKeys.has(key) || !isSerializable(value)) {
      continue;
    }

    sanitized[key] = value;
  }

  return sanitized;
}

function parsePayload(
  rawValue: string | null,
  config: RuntimeWidgetConfig,
): PreferenceValues {
  if (rawValue === null) {
    return {};
  }

  let payload: unknown;
  try {
    payload = JSON.parse(rawValue);
  } catch {
    return {};
  }

  if (
    !isRecord(payload) ||
    payload.schemaVersion !== schemaVersion ||
    payload.projectId !== config.project.id ||
    payload.requestedHostname !== config.domain.requestedHostname ||
    !isRecord(payload.values)
  ) {
    return {};
  }

  return sanitizePreferenceValues(payload.values, config);
}

function createPayload(
  values: PreferenceValues,
  config: RuntimeWidgetConfig,
): PreferencePayload {
  return {
    schemaVersion,
    projectId: config.project.id,
    requestedHostname: config.domain.requestedHostname,
    values: sanitizePreferenceValues(values, config),
  };
}

function canUseStorage(storage: Storage | null | undefined) {
  if (!storage) {
    return false;
  }

  const probeKey = `${storageNamespace}:probe`;
  try {
    storage.setItem(probeKey, "1");
    storage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

export function createPreferenceStorage({
  config,
  storage = getDefaultStorage(),
}: CreatePreferenceStorageInput): WidgetPreferenceStorage {
  const key = buildPreferenceStorageKey(config);
  const storageAvailable = canUseStorage(storage);

  return {
    key,
    read() {
      if (!storageAvailable || !storage) {
        return {};
      }

      try {
        return parsePayload(storage.getItem(key), config);
      } catch {
        return {};
      }
    },
    reset() {
      if (!storageAvailable || !storage) {
        return false;
      }

      try {
        storage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
    write(values) {
      if (!storageAvailable || !storage) {
        return false;
      }

      try {
        storage.setItem(key, JSON.stringify(createPayload(values, config)));
        return true;
      } catch {
        return false;
      }
    },
  };
}
